/**
 * The vault state machine.
 *
 * Exactly one master key is live at a time, held in a module-level variable and
 * never written anywhere in the clear. Everything the UI does goes through here
 * so there is a single place to reason about what is unlocked and how strongly.
 */

import {
  exportRawKey,
  generateMasterKey,
  importAesKey,
  open,
  openJson,
  seal,
  sealJson,
  VaultDecryptionError,
  type Sealed,
} from './aead.js';
import { fromBase64, toBase64 } from './bytes.js';
import * as db from './db.js';
import { CURRENT_KDF, newSaltHex, type KdfParams } from './kdf.js';
import {
  deriveKekB,
  deriveKekP,
  endSession,
  enrolBiometric,
  readSessionKek,
  startSession,
} from './keks.js';
import { anchorPayloadSchema } from './schema.js';
import {
  PAYLOAD_VERSION,
  type AnchorPayload,
  type AnchorRecord,
  type UnlockedAnchor,
  type VaultMetaRecord,
  type WrappedKey,
} from './types.js';

const AAD_MK = 'mk';
const AAD_ANCHOR = 'anchor';
const SESSION_WRAPPED_MK = 'rd.session.mk';

/** How the vault was most recently unlocked. Drives what the UI will reveal. */
export type UnlockStrength = 'locked' | 'session' | 'biometric' | 'passphrase';

export interface VaultState {
  initialised: boolean;
  strength: UnlockStrength;
  /** When the current passphrase proof stops counting as "fresh". */
  passphraseFreshUntil: number;
  exportedAt: number | null;
  biometricEnrolled: boolean;
}

/** A passphrase entry counts as "just now" for this long. */
export const PASSPHRASE_FRESHNESS_MS = 2 * 60 * 1000;

let masterKey: CryptoKey | null = null;
let strength: UnlockStrength = 'locked';
let passphraseFreshUntil = 0;
let cachedMeta: VaultMetaRecord | null = null;

const listeners = new Set<() => void>();
let snapshot: VaultState = {
  initialised: false,
  strength: 'locked',
  passphraseFreshUntil: 0,
  exportedAt: null,
  biometricEnrolled: false,
};

function publish(): void {
  snapshot = {
    initialised: cachedMeta !== null,
    strength,
    passphraseFreshUntil,
    exportedAt: cachedMeta?.exportedAt ?? null,
    biometricEnrolled: Boolean(cachedMeta?.wrappedMkBiometric),
  };
  for (const listener of listeners) listener();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getState(): VaultState {
  return snapshot;
}

export class VaultLockedError extends Error {
  constructor() {
    super('vault is locked');
    this.name = 'VaultLockedError';
  }
}

function requireKey(): CryptoKey {
  if (!masterKey) throw new VaultLockedError();
  return masterKey;
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

export async function load(): Promise<VaultState> {
  cachedMeta = (await db.readMeta()) ?? null;
  publish();
  return snapshot;
}

export async function isInitialised(): Promise<boolean> {
  return (await db.readMeta()) !== undefined;
}

async function wrapMk(kek: CryptoKey, mk: CryptoKey, kind: WrappedKey['kind']): Promise<WrappedKey> {
  const raw = await exportRawKey(mk);
  const sealed = await seal(kek, raw, AAD_MK, kind);
  raw.fill(0);
  return { kind, iv: sealed.iv, ct: sealed.ct, createdAt: Date.now() };
}

async function unwrapMk(kek: CryptoKey, wrapped: WrappedKey): Promise<CryptoKey> {
  const raw = await open(kek, { iv: wrapped.iv, ct: wrapped.ct }, AAD_MK, wrapped.kind);
  const key = await importAesKey(raw);
  raw.fill(0);
  return key;
}

/**
 * Creates a brand-new vault. Fails loudly if one already exists — silently
 * replacing a vault would destroy every hint the user has.
 */
export async function createVault(passphrase: string): Promise<void> {
  if (await isInitialised()) throw new Error('vault already exists on this device');

  const saltHex = newSaltHex();
  const kekP = await deriveKekP(passphrase, saltHex, CURRENT_KDF);
  const mk = await generateMasterKey();

  const meta: VaultMetaRecord = {
    id: 'meta',
    version: 1,
    saltHex,
    kdf: { ...CURRENT_KDF },
    wrappedMkPassphrase: await wrapMk(kekP, mk, 'passphrase'),
    createdAt: Date.now(),
    lastPassphraseVerifiedAt: Date.now(),
    exportedAt: null,
  };

  await db.writeMeta(meta);
  cachedMeta = meta;
  masterKey = mk;
  strength = 'passphrase';
  passphraseFreshUntil = Date.now() + PASSPHRASE_FRESHNESS_MS;
  await cacheForSession(mk);
  publish();
}

export async function unlockWithPassphrase(passphrase: string): Promise<void> {
  const meta = await requireMeta();
  const kekP = await deriveKekP(passphrase, meta.saltHex, meta.kdf as KdfParams);
  const mk = await unwrapMk(kekP, meta.wrappedMkPassphrase);

  masterKey = mk;
  strength = 'passphrase';
  passphraseFreshUntil = Date.now() + PASSPHRASE_FRESHNESS_MS;

  const updated: VaultMetaRecord = { ...meta, lastPassphraseVerifiedAt: Date.now() };
  await db.writeMeta(updated);
  cachedMeta = updated;

  await cacheForSession(mk);
  publish();
}

export async function unlockWithBiometric(): Promise<boolean> {
  const meta = await requireMeta();
  if (!meta.wrappedMkBiometric || !meta.biometricCredentialId) return false;

  const kekB = await deriveKekB(meta.biometricCredentialId);
  if (!kekB) return false;

  masterKey = await unwrapMk(kekB, meta.wrappedMkBiometric);
  strength = 'biometric';
  passphraseFreshUntil = 0;
  await cacheForSession(masterKey);
  publish();
  return true;
}

export async function unlockWithSession(): Promise<boolean> {
  const kekS = await readSessionKek();
  const wrappedJson = sessionStorage.getItem(SESSION_WRAPPED_MK);
  if (!kekS || !wrappedJson) return false;

  try {
    const wrapped = JSON.parse(wrappedJson) as WrappedKey;
    masterKey = await unwrapMk(kekS, wrapped);
  } catch {
    clearSessionCache();
    return false;
  }

  await requireMeta();
  strength = 'session';
  passphraseFreshUntil = 0;
  publish();
  return true;
}

async function cacheForSession(mk: CryptoKey): Promise<void> {
  try {
    const raw = startSession();
    const kekS = await importAesKey(raw);
    raw.fill(0);
    sessionStorage.setItem(SESSION_WRAPPED_MK, JSON.stringify(await wrapMk(kekS, mk, 'session')));
  } catch {
    // sessionStorage can be unavailable (private mode, storage pressure).
    // The session tier is a convenience; losing it is not an error.
  }
}

function clearSessionCache(): void {
  endSession();
  try {
    sessionStorage.removeItem(SESSION_WRAPPED_MK);
  } catch {
    /* nothing to clear */
  }
}

export function lock(): void {
  masterKey = null;
  strength = 'locked';
  passphraseFreshUntil = 0;
  clearSessionCache();
  publish();
}

/** True when the user proved the passphrase recently enough for a deep hint. */
export function hasFreshPassphrase(now = Date.now()): boolean {
  return strength === 'passphrase' && passphraseFreshUntil > now;
}

/**
 * Re-derives KEK-P and checks it against the stored wrap.
 *
 * Used by the level 4/5 gate. It deliberately re-runs Argon2id rather than
 * trusting a flag: the whole point of the gate is that possession of an
 * unlocked tab is not possession of the passphrase.
 */
export async function verifyPassphrase(passphrase: string): Promise<boolean> {
  const meta = await requireMeta();
  try {
    const kekP = await deriveKekP(passphrase, meta.saltHex, meta.kdf as KdfParams);
    await unwrapMk(kekP, meta.wrappedMkPassphrase);
  } catch (error) {
    if (error instanceof VaultDecryptionError) return false;
    throw error;
  }
  passphraseFreshUntil = Date.now() + PASSPHRASE_FRESHNESS_MS;
  strength = 'passphrase';
  publish();
  return true;
}

/** Ends the fresh-passphrase window early, e.g. after the 10s auto-hide fires. */
export function expirePassphraseFreshness(): void {
  passphraseFreshUntil = 0;
  publish();
}

export async function enrolBiometricKek(userId: Uint8Array, userName: string): Promise<boolean> {
  const meta = await requireMeta();
  const mk = requireKey();

  const enrolled = await enrolBiometric(userId, userName);
  if (!enrolled) return false;

  const kekB = await deriveKekB(enrolled.credentialId);
  if (!kekB) return false;

  const updated: VaultMetaRecord = {
    ...meta,
    wrappedMkBiometric: await wrapMk(kekB, mk, 'biometric'),
    biometricCredentialId: enrolled.credentialId,
  };
  await db.writeMeta(updated);
  cachedMeta = updated;
  publish();
  return true;
}

export async function removeBiometricKek(): Promise<void> {
  const meta = await requireMeta();
  const updated = { ...meta };
  delete updated.wrappedMkBiometric;
  delete updated.biometricCredentialId;
  await db.writeMeta(updated);
  cachedMeta = updated;
  publish();
}

// ---------------------------------------------------------------------------
// Anchor payloads
// ---------------------------------------------------------------------------

export async function putAnchor(id: string, payload: AnchorPayload): Promise<void> {
  const mk = requireKey();
  const sealed = await sealJson(mk, payload, AAD_ANCHOR, id);
  const record: AnchorRecord = {
    id,
    iv: sealed.iv,
    ct: sealed.ct,
    updatedAt: Date.now(),
  };
  await db.writeAnchorRecord(record);
}

export async function getAnchor(id: string): Promise<UnlockedAnchor | null> {
  const mk = requireKey();
  const record = await db.readAnchorRecord(id);
  if (!record) return null;
  const raw = await openJson<unknown>(mk, { iv: record.iv, ct: record.ct }, AAD_ANCHOR, id);
  return { id, payload: anchorPayloadSchema.parse(raw) };
}

export async function listAnchors(): Promise<UnlockedAnchor[]> {
  const mk = requireKey();
  const records = await db.listAnchorRecords();
  const out: UnlockedAnchor[] = [];
  for (const record of records) {
    try {
      const raw = await openJson<unknown>(
        mk,
        { iv: record.iv, ct: record.ct },
        AAD_ANCHOR,
        record.id,
      );
      out.push({ id: record.id, payload: anchorPayloadSchema.parse(raw) });
    } catch {
      // A record this key cannot open is not a crash. It means the vault was
      // partially restored or a record predates a re-key; the dashboard shows
      // it as unreadable rather than pretending the anchor does not exist.
    }
  }
  return out;
}

/** Ids present locally, readable without the master key. Used by the heartbeat. */
export async function localAnchorIds(): Promise<string[]> {
  return (await db.listAnchorRecords()).map((r) => r.id);
}

export async function removeAnchor(id: string): Promise<void> {
  await db.deleteAnchorRecord(id);
}

export function emptyPayload(displayName: string): AnchorPayload {
  const now = Date.now();
  return {
    version: PAYLOAD_VERSION,
    displayName,
    storageMedium: '',
    hintLevels: ['', '', '', '', ''],
    questionSequence: [],
    exactLocationHint: '',
    createdAt: now,
    updatedAt: now,
  };
}

// ---------------------------------------------------------------------------
// Internals shared with export/import
// ---------------------------------------------------------------------------

export async function requireMeta(): Promise<VaultMetaRecord> {
  const meta = cachedMeta ?? (await db.readMeta()) ?? null;
  if (!meta) throw new Error('no vault on this device');
  cachedMeta = meta;
  return meta;
}

/** Export needs the raw sealed records; it must not decrypt anything. */
export async function rawAnchorRecords(): Promise<AnchorRecord[]> {
  return db.listAnchorRecords();
}

export async function markExported(at: number): Promise<void> {
  const meta = await requireMeta();
  const updated: VaultMetaRecord = { ...meta, exportedAt: at };
  await db.writeMeta(updated);
  cachedMeta = updated;
  publish();
}

/** Used by import to install a decrypted-and-revalidated vault wholesale. */
export async function installVault(
  meta: VaultMetaRecord,
  anchors: AnchorRecord[],
): Promise<void> {
  await db.replaceAll(meta, anchors);
  cachedMeta = meta;
  publish();
}

export async function destroy(): Promise<void> {
  lock();
  await db.destroyVault();
  cachedMeta = null;
  publish();
}

/** Test seam: re-read everything from a fresh IndexedDB. */
export async function resetForTests(): Promise<void> {
  masterKey = null;
  strength = 'locked';
  passphraseFreshUntil = 0;
  cachedMeta = null;
  db.resetConnectionForTests();
  publish();
}

export type { Sealed };
export { toBase64, fromBase64 };
